import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { productAPI, metaAPI } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useTable from "../../hooks/UseTable";
import TableTopControls from "../../components/TableTopControls";
import TableBottomControls from "../../components/TableBottomControls";
import ConfirmModal from "../../components/ConfirmModal";
import SearchableSelect from "../../components/SearchableSelect";

export default function ProductsAdmin() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '',
    category: '',
    price: '',
    description: ''
  });
  const [categories, setCategories] = useState([]);

  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await metaAPI.getProductCategories();
        setCategories(res.data);
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    };

    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data } = await productAPI.getAll();
      setProducts(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {

      if (!formData.name || !formData.category || !formData.price) {
        toast.error("Name, Category and Price are required");
        return;
      }

      const formDataPayload = new FormData();
      formDataPayload.append("name", formData.name);
      formDataPayload.append("category", formData.category);
      formDataPayload.append("price", formData.price);
      formDataPayload.append("description", formData.description);

      if (editingProduct) {
        await productAPI.update(editingProduct.id, formDataPayload);
        toast.success('Product updated successfully');
      } else {
        await productAPI.create(formDataPayload);
        toast.success('Product created successfully');
      }
      setShowForm(false);
      setEditingProduct(null);
      setFormData({ name: '', category: '', price: '', description: '' });
      fetchProducts();
    } catch (error) {
      toast.error('Failed to save product: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({ name: product.name, category: product.category, price: product.price, description: product.description || '' });
    setShowForm(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await productAPI.delete(deleteId);
      toast.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const {
    paginatedData,
    currentPage,
    totalPages,
    search,
    setSearch,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    startItem,
    endItem,
    totalItems
  } = useTable(products, 5, ["name", "category", "franchise_name"]);

  return (
    <AdminLayout activeItem="Products">
      <header className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-medium text-brand-primary font-outfit">Products</h1>
          {!isSuperAdmin && (
            <button
              onClick={() => {
                setShowForm(true);
                setEditingProduct(null);
                setFormData({ name: '', category: '', price: '', description: '' });
              }}
              className="flex items-center bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Product
            </button>
          )}
        </div>
      </header>

      <main className="p-8" data-testid="products-admin-page">
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="product-form-modal"
            onClick={(e) => { if (e.target === e.currentTarget) { setShowForm(false); setEditingProduct(null); } }}>
            <div className="bg-white p-8 max-w-md w-full mx-4 border border-slate-200">
              <h2 className="text-xl font-medium text-brand-primary mb-6">{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input type="text" required value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent"
                    data-testid="product-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <SearchableSelect
                    value={formData.category}
                    onChange={(val) => setFormData({ ...formData, category: val })}
                    options={categories}
                    placeholder="Select category"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Price (Rs.)</label>
                  <input type="number" min="1" required value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent"
                    data-testid="product-price-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3} className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent"
                    data-testid="product-description-input" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200" data-testid="product-submit-button">
                    {editingProduct ? 'Update' : 'Create'}
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setEditingProduct(null); }}
                    className="flex-1 border border-slate-300 text-slate-700 hover:bg-slate-50 px-6 py-2 transition-all duration-200" data-testid="product-cancel-button">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200">
            <TableTopControls
              search={search}
              setSearch={setSearch}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="products-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {isSuperAdmin && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">
                        Franchise
                      </th>
                    )}
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Category</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Price</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Stock</th>
                    {!isSuperAdmin && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr><td colSpan={6} className="px-6 py-4 text-center text-slate-500">No products found.</td></tr>
                  ) : (
                    paginatedData.map((product) => (
                      <tr key={product.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`product-row-${product.id}`}>
                        {isSuperAdmin && (
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {product.franchise_name || '-'}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm text-slate-800">{product.name}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className="px-3 py-1 bg-brand-cyan-light text-brand-primary text-xs font-medium">
                            {product.category.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-brand-accent">Rs. {product.price}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{product.stock}</td>
                        {!isSuperAdmin && (
                          <td className="px-6 py-4 text-left text-sm text-slate-600">
                            <button onClick={() => handleEdit(product)}
                              className="inline-flex items-center py-1 text-sm text-brand-accent hover:text-brand-accent-hover mr-2"
                              data-testid={`edit-product-${product.id}`}>
                              <Edit className="h-4 w-4 mr-1" /> Edit
                            </button>
                            <button onClick={() => handleDeleteClick(product.id)}
                              className="inline-flex items-center pl-3 py-1 text-sm text-red-600 hover:text-red-700"
                              data-testid={`delete-product-${product.id}`}>
                              <Trash2 className="h-4 w-4 mr-1" /> Delete
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <TableBottomControls
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              startItem={startItem}
              endItem={endItem}
              totalItems={totalItems}
            />
          </div>
        )}

        <ConfirmModal
          open={confirmOpen}
          title="Delete Product"
          message="Are you sure you want to delete this product? This action cannot be undone."
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          confirmText="Delete"
        />

      </main>
    </AdminLayout>
  );
}
